import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { uploadJobPhoto, deleteJobPhoto, jobPhotoUrl, formatApiError } from "@/lib/api";

const MAX = 5;
const MAX_BYTES = 10 * 1024 * 1024;

// Shrink big phone photos before upload; HEIC (non-Safari) falls back to the original file.
async function compress(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size < 1.5 * 1024 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 2560 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.85));
    return blob && blob.size < file.size ? new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }) : file;
  } catch {
    return file;
  }
}

export function PhotoGrid({ photos, srcFor, onDelete, testid = "photo-grid" }) {
  const [zoom, setZoom] = useState(null);
  return (
    <>
      <div data-testid={testid} className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {photos.map((p) => (
          <div key={p.id} className="relative group border border-[#334155] bg-[#0b0e13] aspect-square overflow-hidden">
            <button data-testid={`photo-open-${p.id}`} onClick={() => setZoom(p)} className="w-full h-full">
              <img src={srcFor(p)} alt={p.filename || "Well site photo"} loading="lazy" className="w-full h-full object-cover" />
            </button>
            {onDelete && (
              <button
                data-testid={`photo-delete-${p.id}`}
                onClick={() => onDelete(p)}
                aria-label="Delete photo"
                className="absolute top-1 right-1 bg-[#0b0e13]/80 p-1.5 text-[#94a3b8] hover:text-[#ef4444] transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {zoom && (
        <div data-testid="photo-lightbox" onClick={() => setZoom(null)} className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4">
          <button data-testid="photo-lightbox-close" aria-label="Close" className="absolute top-4 right-4 text-white"><X className="w-6 h-6" /></button>
          <img src={srcFor(zoom)} alt={zoom.filename || "Well site photo"} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </>
  );
}

export function JobPhotosDialog({ job, editable, onClose, onChange }) {
  const input = useRef(null);
  const [photos, setPhotos] = useState(job.photos || []);
  const [busy, setBusy] = useState("");

  const update = (next) => {
    setPhotos(next);
    onChange?.(next);
  };

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX - photos.length;
    if (files.length > room) toast.error(`Only ${room} more photo${room === 1 ? "" : "s"} allowed (max ${MAX})`);
    let next = photos;
    for (const [i, raw] of files.slice(0, room).entries()) {
      setBusy(`Uploading ${i + 1} of ${Math.min(files.length, room)}…`);
      const file = await compress(raw);
      if (file.size > MAX_BYTES) {
        toast.error(`${raw.name} is over 10MB`);
        continue;
      }
      try {
        next = [...next, await uploadJobPhoto(job.id, file)];
        update(next);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || `Could not upload ${raw.name}`);
      }
    }
    setBusy("");
  };

  const remove = async (p) => {
    try {
      await deleteJobPhoto(job.id, p.id);
      update(photos.filter((x) => x.id !== p.id));
    } catch {
      toast.error("Could not delete photo");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="job-photos-dialog" className="bg-[#11151c] border-[#334155] text-[#f8fafc] max-w-2xl">
        <DialogHeader>
          <DialogTitle className="heading">Well site photos · {job.name}</DialogTitle>
          <DialogDescription className="text-[#94a3b8] text-xs mono">
            {photos.length}/{MAX} photos · JPG, PNG, WEBP or HEIC up to 10MB each
          </DialogDescription>
        </DialogHeader>
        {photos.length ? (
          <PhotoGrid photos={photos} srcFor={(p) => jobPhotoUrl(job.id, p.id)} onDelete={editable ? remove : null} testid="job-photos-grid" />
        ) : (
          <div data-testid="job-photos-empty" className="border border-dashed border-[#334155] p-8 text-center text-sm text-[#94a3b8]">
            <Camera className="w-6 h-6 mx-auto mb-2 text-[#64748b]" /> No photos yet.
          </div>
        )}
        {editable && (
          <>
            <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple hidden onChange={pick} data-testid="job-photos-input" />
            <button
              data-testid="job-photos-upload-btn"
              disabled={!!busy || photos.length >= MAX}
              onClick={() => input.current?.click()}
              className="inline-flex items-center justify-center gap-2 bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-4 py-3 mono text-xs tracking-widest uppercase font-bold disabled:opacity-40 transition-colors"
            >
              <Upload className="w-4 h-4" /> {busy || (photos.length >= MAX ? "Photo limit reached" : "Add photos")}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
